import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { findBirthRecordFor } from '../../pedigree-tool/pedigree/liveBirthInheritance';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import {
  PREG_ACCENT,
  recordCardTint,
  recordCardTintBorder
} from './clinical-notes-section/recordCardStyles';

// "Born of a recorded pregnancy" — the notice on a person who was created FROM a live birth, with a
// link to the carrier whose record holds it (REQ-I6 direction 2: without it the link is one-way, so
// you could reach the child from the pregnancy but never the pregnancy from the child).
//
// This lives in the PERSON section, not Reproductive (TGCLOUD-1617 item 5). Kathryn: "this is the
// person that's being generated out of the pregnancy — that should not appear as a reproductive
// record, because at a quick glance it just looks like there's a pregnancy record for that person… I
// would put that under the person record and leave reproductive options only for the pregnancies of
// that individual." She is right that a notice sharing a list with the person's OWN pregnancy tiles
// conflates two different things. The notice itself is wanted — only its home changes.
//
// Consequences worth knowing:
//   * Reproductive now contains ONLY the individual's own pregnancies, which is what lets item 6 hide
//     that section outright for an assigned-male person without hiding this.
//   * The notice is not restricted to live births — it keys on `bornMemberId`, so it covers a
//     stillbirth-created person automatically once TGCLOUD-1616 lands, with no further work.
//
// Renders nothing when the person was not born of a record, so the caller can drop it in
// unconditionally.
export default function BornOfPregnancyNotice({
  member,
  memberRecords,
  onSelectMember
}) {
  const fmtDate = useDateFormatter();
  const birthRecord = findBirthRecordFor(memberRecords, member);
  if (!birthRecord) return null;

  const carrier = birthRecord.carrier;
  const carrierLabel =
    [carrier?.firstName, carrier?.lastName].filter(Boolean).join(' ') ||
    (carrier ? `Person ${carrier.pid}` : '');
  // Not named in TGCLOUD-1707, but the same field read the same wrong way — a
  // hardcoded 'en-GB' that ignores the configured date format. Fixing the
  // fetus-event copy and leaving this one is how the rule drifts across copies in
  // the first place. `dateOfRecord` is an instant column, hence
  // toLocalDateFromInstant.
  const formatted = fmtDate(
    toLocalDateFromInstant(birthRecord.record?.dateOfRecord)
  );
  const dateLabel = formatted ? ` · ${formatted}` : '';

  return (
    <Box
      sx={{
        border: `1px solid ${recordCardTintBorder(PREG_ACCENT)}`,
        borderLeft: `3px solid ${PREG_ACCENT}`,
        backgroundColor: recordCardTint(PREG_ACCENT),
        borderRadius: '6px',
        px: 1.5,
        py: 1,
        mb: 2
      }}
    >
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: PREG_ACCENT,
          lineHeight: 1
        }}
      >
        Born of a recorded pregnancy
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {carrierLabel}
        {dateLabel}
      </Typography>
      {onSelectMember && (
        <Button
          size="small"
          onClick={() => onSelectMember(carrier.pid)}
          sx={{ mt: 0.5, px: 0 }}
        >
          Open that record
        </Button>
      )}
    </Box>
  );
}
